import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import useDebounce from "../../../app/customHooks/useDebounce";
import { useGetAllTasksListWithPaginationMutation } from "../../../app/services/taskAPI";
import { decryptUrlData } from "../../../services/CryptoService";
import { useGroupBy } from "../../../utils/Context/TaskGroupByContext";
import { useTaskFilter } from "../../../utils/Context/TaskFilterContext";
import { normalizeTaskGridRows } from "../../../components/tasks/adapters/normalizeTaskGridRow";

const PAGE_SIZE = 50;

const readTotalCount = (row) => {
    if (!row || typeof row !== "object") return null;
    const raw = row.totalCount ?? row.TotalCount;
    if (raw === undefined || raw === null || raw === "") return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
};

const normalizePagedTaskResponse = (data) => {
    if (!data) {
        return { items: [], totalCount: null };
    }

    if (Array.isArray(data)) {
        const rowTotal = data.length ? readTotalCount(data[0]) : 0;
        const totalCount = data.length > 0 && rowTotal === 0 ? null : rowTotal;
        return { items: data, totalCount };
    }

    if (typeof data === "object") {
        const items = Array.isArray(data.items)
            ? data.items
            : (Array.isArray(data.data) ? data.data : []);
        const wrapperRaw = data.totalCount ?? data.TotalCount;
        const wrapperTotal = wrapperRaw === undefined || wrapperRaw === null || wrapperRaw === ""
            ? null
            : Number(wrapperRaw);
        let totalCount = Number.isFinite(wrapperTotal) ? wrapperTotal : readTotalCount(items[0]);
        if (items.length > 0 && totalCount === 0) {
            totalCount = null;
        }
        return { items, totalCount };
    }

    return { items: [], totalCount: null };
};

const mergeTasksById = (prev, next) => {
    if (!next?.length) return prev || [];
    if (!prev?.length) return next;

    const seen = new Set(prev.map((task) => task.taskId));
    const appended = [];

    next.forEach((task) => {
        if (task?.taskId == null || seen.has(task.taskId)) return;
        seen.add(task.taskId);
        appended.push(task);
    });

    return appended.length ? [...prev, ...appended] : prev;
};

const useProjectTaskList = ({ getExtraFilter } = {}) => {
    const { id } = useParams();
    const user = useSelector((state) => state.auth.user);
    const { filterRequest, resetFilters } = useTaskFilter();
    const { selectedSort, selectedOrder, resetGroupBy, handleDeleteClick } = useGroupBy();

    const [taskList, setTaskList] = useState([]);
    const [showCompleted, setShowCompleted] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [totalCount, setTotalCount] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const requestedPageRef = useRef(1);
    const debouncedSearch = useDebounce(searchText, 500);

    let projectId = null;
    let projectName = "";
    let projectCode = "";

    const projectdata = id ? decryptUrlData(id) : null;

    if (typeof projectdata === "object" && projectdata !== null) {
        projectId = projectdata.projectId;
        projectName = projectdata.projectName;
        projectCode = projectdata.projectCode;
    } else if (projectdata) {
        projectId = projectdata;
    }

    const extraFilter = useMemo(() => {
        return getExtraFilter?.({
            projectId,
            userId: user?.userId,
        }) || { Field: "ProjectId", Operator: "Is", Value: projectId?.toString() || "0" };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, user?.userId]);

    const [
        getAllTasksWithDynamicFilters,
        {
            isLoading: isTaskLoading,
            isSuccess: isTaskSuccess,
            isError: isTaskError,
            data: taskListData,
        },
    ] = useGetAllTasksListWithPaginationMutation();

    const getAllTaskList = useCallback(
        (activeFilters = filterRequest, includeCompleted = showCompleted, page = currentPage, search = debouncedSearch) => {
            requestedPageRef.current = page;
            setIsLoadingMore(page > 1);

            const cleanedFilterRequest = (activeFilters || []).filter(
                (item) =>
                    item.Field?.trim() !== "" ||
                    item.Operator?.trim() !== "" ||
                    (item.Value !== undefined && item.Value !== null && item.Value !== "")
            );

            const request = {
                searchText: search,
                sortColumn: selectedSort?.value || "TaskId",
                sortDirection: selectedOrder?.value || "ASC",
                projectId: projectId,
                userId: 0,
                filterData: JSON.stringify([...cleanedFilterRequest, extraFilter]),
                showCompleted: includeCompleted,
                pagination: {
                    pageNumber: page,
                    pageSize: PAGE_SIZE,
                },
            };

            getAllTasksWithDynamicFilters(request);
        },
        [
            currentPage,
            debouncedSearch,
            extraFilter,
            filterRequest,
            getAllTasksWithDynamicFilters,
            projectId,
            selectedOrder,
            selectedSort,
            showCompleted,
        ]
    );

    const handleRefreshGrid = useCallback(() => {
        setCurrentPage(1);
        getAllTaskList(filterRequest, showCompleted, 1, debouncedSearch);
    }, [debouncedSearch, filterRequest, getAllTaskList, showCompleted]);

    const onToggleChange = useCallback((value) => {
        setShowCompleted(value);
        setCurrentPage(1);
    }, []);

    const handleLoadMore = useCallback(() => {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        getAllTaskList(filterRequest, showCompleted, nextPage, debouncedSearch);
    }, [currentPage, debouncedSearch, filterRequest, getAllTaskList, showCompleted]);

    const onSearchChange = useCallback((text) => {
        setSearchText(text);
    }, []);

    useEffect(() => {
        if (!projectId) return;
        setCurrentPage(1);
        getAllTaskList(filterRequest, showCompleted, 1, debouncedSearch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterRequest, projectId, debouncedSearch, showCompleted, extraFilter, selectedSort, selectedOrder]);

    useEffect(() => {
        if (!isTaskSuccess || taskListData == null) return;

        const { items, totalCount: serverTotal } = normalizePagedTaskResponse(taskListData);
        const normalizedItems = normalizeTaskGridRows(items);
        const isFirstPage = requestedPageRef.current === 1;

        setHasLoadedOnce(true);
        setIsLoadingMore(false);
        if (isFirstPage || (serverTotal != null && normalizedItems.length > 0)) {
            setTotalCount(serverTotal);
        }

        if (isFirstPage) {
            setTaskList(normalizedItems);
            setHasMore(serverTotal != null ? normalizedItems.length < serverTotal : normalizedItems.length >= PAGE_SIZE);
            return;
        }

        setTaskList((prev) => {
            const merged = mergeTasksById(prev, normalizedItems);
            const addedCount = merged.length - (prev?.length || 0);
            if (addedCount <= 0) {
                setHasMore(false);
            } else {
                setHasMore(serverTotal != null ? merged.length < serverTotal : normalizedItems.length >= PAGE_SIZE);
            }
            return merged;
        });
    }, [isTaskSuccess, taskListData]);

    useEffect(() => {
        if (isTaskError) {
            setHasLoadedOnce(true);
            setIsLoadingMore(false);
            if (requestedPageRef.current === 1) {
                setHasMore(false);
            }
        }
    }, [isTaskError]);

    useEffect(() => {
        return () => {
            resetFilters?.();
            (resetGroupBy || handleDeleteClick)?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isInitialLoad = isTaskLoading && !hasLoadedOnce;
    const isRefreshing = isTaskLoading && hasLoadedOnce && !isLoadingMore;

    // Only filterRequest is cleared by resetFilters, so the two are reported
    // separately: search drives the empty-state wording, filters drive the
    // Clear filters action.
    const hasActiveFilters = (filterRequest || []).length > 0;
    const hasActiveSearch = Boolean(debouncedSearch?.trim());

    const patchTask = useCallback((taskId, patch) => {
        if (taskId == null) return;
        setTaskList((prev) => prev.map((task) => (
            task.taskId === taskId ? { ...task, ...patch } : task
        )));
    }, []);

    return {
        projectId,
        projectName,
        projectCode,
        cardData: projectCode && projectName ? [{ key: projectCode, value: projectName }] : [],
        taskList,
        showCompleted,
        hasMore,
        totalCount,
        isInitialLoad,
        isRefreshing,
        isLoadingMore,
        hasActiveFilters,
        hasActiveSearch,
        isError: Boolean(isTaskError),
        onToggleChange,
        onSearchChange,
        handleRefreshGrid,
        handleLoadMore,
        handleRetry: handleRefreshGrid,
        patchTask,
    };
};

export default useProjectTaskList;
